import React from 'react';
import { toast } from 'react-toastify';
import { useGetPaymentProcessorSetupQuery, useValidateStripeMutation } from '../../../store/endpoints/StorefrontSettingsEndpoints';
import { DocumentCard, DocumentCardType, DocumentCardPreview, DocumentCardTitle, DocumentCardStatus, DocumentCardDetails, DocumentCardActions, ImageFit } from '@fluentui/react';
import StripeSetupDialog from './StripeSetupDialog';
import StripeConfigPanel from './StripeConfigPanel';
import { useUrlPanelState } from '../../../hooks/useUrlState';

const PaymentProcessorDetails: React.FC = () => {
    const [setupStripe, setSetupStripe] = React.useState<boolean>(false);
    // In the URL (?panel=stripeConfig) so documentation can link to the settings it holds.
    const stripeConfigPanel = useUrlPanelState('stripeConfig');
    const setupProcessors = useGetPaymentProcessorSetupQuery();
    const [validateStripe] = useValidateStripeMutation();

    const validate = async () => {
        const toastId = toast.loading('Validating Stripe setup…');
        try {
            const result = await validateStripe({ returnUrl: window.location.href }).unwrap();
            if (result.success && result.url) {
                toast.update(toastId, { type: 'success', render: 'Validated', isLoading: false, autoClose: 3000 });
                window.open(result.url, '_blank');
            } else {
                toast.update(toastId, { type: 'error', render: `Error: ${result.error || 'Validation failed'}`, isLoading: false, autoClose: 5000 });
            }
        } catch {
            toast.update(toastId, { type: 'error', render: 'Error: Validation failed', isLoading: false, autoClose: 5000 });
        }
    };

    return <>
        <DocumentCard
            type={DocumentCardType.compact}
            style={{ width: '250px', height: '110px' }}
        >
            <DocumentCardPreview
                previewImages={[{
                    previewImageSrc: '/stripe.svg',
                    imageFit: ImageFit.contain,
                    width: 60,
                }]}
                styles={{ root: { padding: '0.5em', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: '2em', paddingRight: '2em' } }}
                />
                <DocumentCardDetails styles={{ root: { padding: '0.5em 0.75em', justifyContent: 'space-between' } }}>
                    <DocumentCardTitle title='Stripe' styles={{ root: { padding: 0, height: 'auto' } }} />
                    <DocumentCardStatus
                        status={setupProcessors.data?.stripeSetup ? 'Setup' : 'Not Setup'}
                        statusIcon={setupProcessors.data?.stripeSetup ? 'CheckMark' : 'ChromeClose'}
                        styles={{ root: { margin: 0 } }}
                        />
                    <DocumentCardActions
                        actions={[
                            { iconProps: { iconName: 'Permissions' }, onClick: () => setSetupStripe(true), ariaLabel: 'Enter Stripe key' },
                            { iconProps: { iconName: 'Settings' }, onClick: () => stripeConfigPanel.open(), ariaLabel: 'Configure Stripe' },
                            { iconProps: { iconName: 'TestBeaker' }, onClick: validate, ariaLabel: 'Validate Stripe setup', disabled: !setupProcessors.data?.stripeSetup },
                        ]}
                        styles={{ root: { padding: 0 } }}
                        />
                </DocumentCardDetails>
        </DocumentCard>
        <StripeSetupDialog isOpen={setupStripe} onDismiss={() => setSetupStripe(false)} />
        <StripeConfigPanel isOpen={stripeConfigPanel.isOpen} onDismiss={() => stripeConfigPanel.close()} />
    </>
}

export default PaymentProcessorDetails;
